import useSWR from 'swr';
import { cn } from '@/lib/utils';
import type { LiveSnapshot } from '@/types/live';
import { GithubGlyph, YoutubeGlyph } from './Glyphs';
import {
  extractGithubHandle,
  fetchSnapshot,
  githubUrl,
  initialsFor,
  toEmbedUrl,
} from './live-fetch';
import { LiveVoteButton } from './LiveVoteButton';

// The stage view every edition without a live directory of its own gets
// (app/(public)/[locale]/live/_components/live-view.tsx): no header, `font-title`, and an
// upvote counter next to each project that links to its voting page.
export function LiveView({
  eventSlug,
  initialData,
}: {
  eventSlug: string;
  initialData: LiveSnapshot;
}) {
  const { data } = useSWR<LiveSnapshot>(
    `/api/${eventSlug}/live`,
    fetchSnapshot,
    {
      fallbackData: initialData,
      refreshInterval: 3000,
      revalidateOnFocus: true,
    },
  );

  const live = data ?? initialData;
  const embedUrl = toEmbedUrl(live.youtubeUrl);
  const current = live.current;

  return (
    <div className="bg-background text-primary lg:h-screen lg:overflow-hidden">
      <div className="flex flex-col gap-4 p-3 sm:p-4 md:gap-6 md:p-8 lg:h-full lg:flex-row lg:gap-0 lg:p-0">
        <div className="flex flex-1 flex-col gap-3 lg:min-h-0 lg:p-6 xl:p-8">
          <div className="flex items-center justify-center lg:min-h-0 lg:flex-1">
            <div className="aspect-video max-h-full w-full border-2 border-primary bg-background/80 backdrop-blur-sm">
              <div className="relative h-full w-full bg-black">
                {embedUrl ? (
                  <iframe
                    key={embedUrl}
                    src={embedUrl}
                    title={`${live.event.name} live`}
                    className="absolute inset-0 h-full w-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="font-bold font-title text-2xl text-primary uppercase">
                      Stream not configured
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {live.youtubeUrl && (
              <a
                href={`/${live.event.slug}/live/source`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-primary px-3 py-1.5 font-mono text-primary text-xs uppercase transition-colors hover:bg-primary hover:text-background"
              >
                <YoutubeGlyph className="h-3.5 w-3.5" />
                Ver en YouTube
              </a>
            )}
            {current?.project.repoUrl && (
              <a
                href={current.project.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-primary px-3 py-1.5 font-mono text-primary text-xs uppercase transition-colors hover:bg-primary hover:text-background"
              >
                <GithubGlyph className="h-3.5 w-3.5" />
                Repo
              </a>
            )}
          </div>
        </div>

        <aside className="flex w-full flex-col gap-3 sm:gap-4 lg:h-full lg:w-[380px] lg:shrink-0 lg:gap-4 lg:overflow-y-auto lg:border-primary lg:border-l-2 lg:p-4 xl:w-[420px] xl:p-6">
          <CurrentCard
            eventSlug={live.event.slug}
            current={current}
            viewer={live.viewer}
          />
          <PastCard
            eventSlug={live.event.slug}
            past={live.past}
            viewer={live.viewer}
          />
        </aside>
      </div>
    </div>
  );
}

function CurrentCard({
  eventSlug,
  current,
  viewer,
}: {
  eventSlug: string;
  current: LiveSnapshot['current'];
  viewer: LiveSnapshot['viewer'];
}) {
  if (!current) {
    return (
      <div className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm sm:p-6">
        <h2 className="font-bold font-title text-primary/70 text-sm uppercase tracking-wide">
          En vivo
        </h2>
        <p className="mt-3 font-title text-primary/70">
          Esperando próxima presentación…
        </p>
      </div>
    );
  }

  const initials = initialsFor(current.project.name);

  return (
    <div className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm sm:p-6">
      <div className="flex items-start gap-3 border-primary/20 border-b pb-4 sm:gap-4">
        <div
          className={cn(
            'flex h-14 w-14 shrink-0 items-center justify-center border-2 border-primary sm:h-16 sm:w-16',
            current.project.logoUrl ? 'bg-background' : 'bg-primary',
          )}
        >
          {current.project.logoUrl ? (
            <img
              src={current.project.logoUrl}
              alt={current.project.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="font-bold font-title text-2xl text-background">
              {initials || '?'}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h1 className="min-w-0 font-bold font-title text-primary text-xl leading-tight sm:text-2xl md:text-3xl">
              {current.project.name}
            </h1>
            <div className="hidden sm:block">
              <LiveVoteButton
                eventSlug={eventSlug}
                projectSlug={current.project.slug}
                voted={viewer.votedProjectIds.includes(current.project.id)}
                count={current.voteCount}
                size="md"
              />
            </div>
          </div>
          {current.project.oneliner && (
            <p className="mt-1 font-title text-primary/70 text-sm">
              {current.project.oneliner}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 sm:hidden">
        <LiveVoteButton
          eventSlug={eventSlug}
          projectSlug={current.project.slug}
          voted={viewer.votedProjectIds.includes(current.project.id)}
          count={current.voteCount}
          size="lg"
          fullWidth
          showLabel
        />
      </div>

      {current.track && (
        <div className="mt-4 border-primary border-l-4 pl-4">
          <h3 className="font-bold font-title text-primary/70 text-xs uppercase">
            Track
          </h3>
          <p className="mt-1">
            <span className="bg-primary px-2 py-1 font-bold font-title text-background">
              {current.track.name}
            </span>
          </p>
        </div>
      )}

      <div className="mt-4 border-primary border-l-4 pl-4">
        <h3 className="font-bold font-title text-primary/70 text-xs uppercase">
          Equipo
        </h3>
        <p className="mt-1">
          <span className="bg-primary px-2 py-1 font-bold font-title text-background">
            /{current.team.slug}
          </span>
        </p>
      </div>

      {current.team.members.length > 0 && (
        <div className="mt-4 border-primary border-l-4 pl-4">
          <h3 className="font-bold font-title text-primary/70 text-xs uppercase">
            Miembros
          </h3>
          <ul className="mt-2 flex flex-col gap-2">
            {current.team.members.map((m) => {
              const handle = extractGithubHandle(m.github);
              const href = githubUrl(m.github);
              return (
                <li key={m.fullName} className="flex flex-col">
                  <span className="font-bold font-title text-primary">
                    {m.fullName}
                  </span>
                  {handle &&
                    (href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-primary/70 text-xs hover:text-primary hover:underline"
                      >
                        @{handle}
                      </a>
                    ) : (
                      <span className="font-mono text-primary/70 text-xs">
                        @{handle}
                      </span>
                    ))}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {current.mentor && (
        <div className="mt-4 border-primary border-l-4 pl-4">
          <h3 className="font-bold font-title text-primary/70 text-xs uppercase">
            Mentor
          </h3>
          <p className="mt-1 font-bold font-title text-primary">
            {current.mentor.fullName}
          </p>
        </div>
      )}
    </div>
  );
}

function PastCard({
  eventSlug,
  past,
  viewer,
}: {
  eventSlug: string;
  past: LiveSnapshot['past'];
  viewer: LiveSnapshot['viewer'];
}) {
  if (past.length === 0) return null;
  return (
    <div className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm sm:p-6">
      <h2 className="font-bold font-title text-primary/70 text-sm uppercase tracking-wide">
        Anteriores
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {past.map((p) => {
          const initials = initialsFor(p.name);
          return (
            <li
              key={p.projectId}
              className="flex items-center gap-2.5 border-primary border-l-2 pl-2.5 sm:gap-3 sm:border-l-4 sm:pl-3"
            >
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center border border-primary sm:h-9 sm:w-9',
                  p.logoUrl ? 'bg-background' : 'bg-primary',
                )}
              >
                {p.logoUrl ? (
                  <img
                    src={p.logoUrl}
                    alt={p.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="font-bold font-title text-background text-xs">
                    {initials || '?'}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold font-title text-primary text-sm">
                  {p.name}
                </p>
                <p className="truncate font-mono text-primary/60 text-xs">
                  /{p.teamSlug}
                </p>
              </div>
              <LiveVoteButton
                eventSlug={eventSlug}
                projectSlug={p.projectSlug}
                voted={viewer.votedProjectIds.includes(p.projectId)}
                count={p.voteCount}
                size="sm"
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
